import { useTranslation } from "react-i18next";
import { Bar, CartesianGrid, ComposedChart, ReferenceLine, ResponsiveContainer, Tooltip, XAxis, YAxis } from "recharts";
import type { OrganisationTrendResponse } from "../../api/organisationReporting";
import { ChartDefs } from "../charts/chartEffects";
import { getVendorColor } from "../charts/chartColors";
import { ChartTooltip } from "../charts/ChartTooltip";
import { SeriesLegend, type SeriesLegendEntry } from "../charts/SeriesLegend";
import { useChartEffectIds } from "../charts/useChartEffectIds";
import { useSeriesVisibility } from "../charts/useSeriesVisibility";
import panelStyles from "../consumption/ConsumptionSummary.module.css";
import { formatRoundedMoney } from "../consumption/formatters";
import { coverageStarts, financialYearName, financialYearStartMonth, monthLabel } from "./overviewFigures";
import styles from "./Overview.module.css";

/** The key of the running month's projected remainder, which no vendor's id can be. */
const projectedKey = "\u0000projected";

/**
 * Spend, month by month: a column per month stacked by tool, the running month's projected remainder drawn hatched on top, and a
 * mark at the month each tool's spend begins to be recorded, so a jump there is read as the recording starting.
 *
 * @param props.trend - The trend.
 * @param props.projected - The running month's projected total, or null.
 */
export function SpendTrendPanel({ trend, projected }: { trend: OrganisationTrendResponse; projected: number | null }): React.JSX.Element {
  const { t } = useTranslation();
  const { isHidden, toggle } = useSeriesVisibility();
  const effects = useChartEffectIds();

  const vendors = trend.vendors.filter((vendor) => trend.months.some((month) => (month.vendors.find((v) => v.vendorId === vendor.vendorId)?.spend ?? 0) > 0));
  const colours = new Map(vendors.map((vendor, index) => [vendor.vendorId, getVendorColor(vendor.vendorId, index)]));
  const total = trend.months.reduce((sum, month) => sum + (month.totalSpend ?? 0), 0);
  const labels = new Map(trend.months.map((month, index) => [month.periodStart, monthLabel(month.periodStart, index === 0 || month.periodStart.endsWith("-01-01"))]));

  const data = trend.months.map((month) => {
    const point: Record<string, number | string | null> = { name: labels.get(month.periodStart) ?? month.periodStart };
    for (const vendor of vendors) {
      point[vendor.vendorId] = month.vendors.find((v) => v.vendorId === vendor.vendorId)?.spend ?? 0;
    }
    point[projectedKey] = month.isCurrentMonth && projected !== null ? Math.max(0, projected - (month.totalSpend ?? 0)) : null;
    return point;
  });
  const hasProjection = data.some((point) => (point[projectedKey] as number | null) !== null);

  const legend: SeriesLegendEntry[] = [
    ...vendors.map((vendor) => ({ key: vendor.vendorId, label: vendor.displayName, color: colours.get(vendor.vendorId) ?? "" })),
    // The projection is not a tool, so it is drawn in the neutral secondary colour, never one a tool could have.
    ...(hasProjection ? [{ key: projectedKey, label: t("overview.spend.projected"), color: "var(--color-text-secondary)" }] : []),
  ];
  const starts = coverageStarts(trend, "spend");
  // Where a financial year starts within the run, marked across the chart so the year's months read as one block.
  const yearStarts = trend.months
    .slice(1)
    .filter((month) => Number(month.periodStart.slice(5, 7)) === financialYearStartMonth)
    .map((month) => month.periodStart);
  const lastSeries = vendors[vendors.length - 1]?.vendorId;

  return (
    <section className={`${panelStyles.forecastPanel} ${styles.wide}`} data-spotlight aria-labelledby="overview-spend-heading">
      <h2 id="overview-spend-heading" className={`panel-title ${panelStyles.panelTitle}`}>
        {t("overview.spend.title")}
      </h2>
      <p className={styles.subtitle}>{t("overview.spend.subtitle", { total: formatRoundedMoney(total, trend.currency), count: trend.months.length })}</p>
      <div className={panelStyles.chartContainer} role="img" aria-label={t("overview.spend.chartLabel")}>
        <ResponsiveContainer width="100%" height={280}>
          <ComposedChart data={data} margin={{ top: 24, right: 16, bottom: 8, left: 8 }}>
            <ChartDefs
              ids={effects}
              gradients={[
                ...vendors.map((vendor) => ({ key: vendor.vendorId, color: colours.get(vendor.vendorId) ?? "", top: 1, bottom: 0.55 })),
                { key: "projected", color: "var(--color-text-secondary)", top: 0.3, bottom: 0.08 },
              ]}
            />
            <CartesianGrid vertical={false} stroke="color-mix(in srgb, var(--color-border-default) 70%, transparent)" strokeDasharray="4 4" />
            <XAxis dataKey="name" axisLine={false} tickLine={false} interval={0} tick={{ fill: "var(--color-text-secondary)", fontSize: 12 }} />
            <YAxis
              axisLine={false}
              tickLine={false}
              tick={{ fill: "var(--color-text-secondary)", fontSize: 12 }}
              tickFormatter={(value: number) => formatRoundedMoney(value, trend.currency)}
              width={72}
            />
            <Tooltip
              content={
                <ChartTooltip
                  colors={new Map(legend.map((entry) => [entry.key, entry.color]))}
                  formatLabel={(label) => label}
                  formatValue={(value) => formatRoundedMoney(value, trend.currency)}
                />
              }
              cursor={{ fill: "color-mix(in srgb, var(--color-chart-blue) 10%, transparent)" }}
            />
            {vendors.map((vendor) => (
              <Bar
                key={vendor.vendorId}
                stackId="spend"
                dataKey={vendor.vendorId}
                name={vendor.displayName}
                fill={`url(#${effects.gradient(vendor.vendorId)})`}
                radius={!hasProjection && vendor.vendorId === lastSeries ? [4, 4, 0, 0] : 0}
                hide={isHidden(vendor.vendorId)}
              />
            ))}
            {hasProjection && (
              <Bar
                stackId="spend"
                dataKey={projectedKey}
                name={t("overview.spend.projected")}
                fill={`url(#${effects.gradient("projected")})`}
                stroke="var(--color-text-secondary)"
                strokeDasharray="4 3"
                radius={[4, 4, 0, 0]}
                hide={isHidden(projectedKey)}
              />
            )}
            {yearStarts.map((periodStart) => (
              <ReferenceLine
                key={`year-${periodStart}`}
                x={labels.get(periodStart)}
                stroke="var(--color-text-secondary)"
                strokeOpacity={0.5}
                label={{ value: t("overview.spend.yearStart", { name: financialYearName(periodStart) }), position: "insideTopRight", fill: "var(--color-text-secondary)", fontSize: 11 }}
              />
            ))}
            {starts.map((start) => (
              <ReferenceLine
                key={start.vendorId}
                x={labels.get(start.periodStart)}
                stroke={colours.get(start.vendorId) ?? "var(--color-text-secondary)"}
                strokeDasharray="3 4"
                strokeOpacity={0.7}
              />
            ))}
          </ComposedChart>
        </ResponsiveContainer>
      </div>
      <SeriesLegend entries={legend} isHidden={isHidden} onToggle={toggle} />
      {starts.length > 0 && (
        <p className={styles.note}>
          {t("overview.spend.coverage", {
            list: starts.map((start) => t("overview.spend.coverageItem", { tool: start.displayName, month: monthLabel(start.periodStart, true) })).join(", "),
          })}
        </p>
      )}
    </section>
  );
}
